// @owners { team: patients }
import { ActionSheetContext, Button, Card, Toast, ToastContext, XsSpacing } from '@alto/design-system';
import {
  ExcessSupplyContactProgynyActionSheet,
  LeftoverMedInput,
  LeftoverMedsInfoAndContactProgynyActionSheet,
  MEDICATION_TYPES_DISPLAY,
  ProgynyWizardStepsHeaderContent,
  getProgynyWizardNextRoute,
} from '@alto/features';
import { useNavigation } from '@alto/navigation';
import {
  type FertilityCycleMedicationType,
  FertilityCycleMedicationTypeMap,
} from '@alto/scriptdash/alto/fertility_cycles/types/v1/fertility_cycle_medication_type';
import React, { useCallback, useContext, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { TypescriptUtils } from '~shared/TypescriptUtils';
import { clearFetchDeliveriesError } from '~shared/actions/deliveries';
import { clearAutobillPrescriptionError, clearPrescriptionsError } from '~shared/actions/prescriptions';
import { updateMedicationsSupplyOnHand } from '~shared/actions/ui/progynyWizard';
import getErrors from '~shared/features/ui/selectors/getErrors';
import { getErrorMessage } from '~shared/helpers/helper';
import { usePageViewedEvent } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { getProgynyWizardFirstFill } from '~shared/selectors/checkout/progyny';
import { getDaysSupplyOnHandByMedType } from '~shared/selectors/progyny-wizard/getDaysSupplyOnHandByMedType';
import { getPrescriptionIDsByMedType } from '~shared/selectors/progyny-wizard/getPrescriptionIDsByMedType';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { ProgynyPage } from '../../shared/components/ProgynyPage';
import { useCalculateProgynyOrderQuantities } from '../hooks/useCalculateProgynyOrderQuantities';

type Values = Partial<Record<FertilityCycleMedicationType, number>>;

type MedicationSupplyOnHand = {
  medication_type: FertilityCycleMedicationType;
  supply_on_hand: { prescription_id: number | null | undefined; days_supply: number };
}[];

export const LeftoverMeds = () => {
  const { setActiveActionSheet } = useContext(ActionSheetContext);
  const dispatch = useDispatchShared();
  const firstFill = useSelectorShared(getProgynyWizardFirstFill);
  const isRefill = !firstFill;
  const { calculateQuantities } = useCalculateProgynyOrderQuantities();

  const { goBack, getCurrentRouteName, navigate } = useNavigation();
  const initialValues = useSelectorShared(getDaysSupplyOnHandByMedType);
  const medicationTypes = TypescriptUtils.objectKeys(initialValues);

  const currentRoute = getCurrentRouteName();
  const nextRouteName = getProgynyWizardNextRoute(currentRoute);
  const prescriptionIDsByMedType = useSelectorShared(getPrescriptionIDsByMedType);

  const { addToast } = useContext(ToastContext);
  const { autobillPrescriptionError, fetchPrescriptionsError, fetchDeliveriesError } = useSelectorShared(getErrors);
  const error = autobillPrescriptionError || fetchPrescriptionsError || fetchDeliveriesError;

  const handleShowLeftoverMedsActionSheet = useCallback(() => {
    setActiveActionSheet(<LeftoverMedsInfoAndContactProgynyActionSheet />);
  }, [setActiveActionSheet]);

  const showExcessSupplyActionSheet = useCallback(
    (medicationNames: (string | null)[]) => {
      setActiveActionSheet(<ExcessSupplyContactProgynyActionSheet medicationNames={medicationNames} />);
    },
    [setActiveActionSheet],
  );

  const onSubmit = useCallback(
    (values: Values) => {
      const excessSupplyMeds = Object.entries(values)
        .map(([medType, daysSupply]) => {
          const daysSupplyNumber = daysSupply || 0;
          if (
            (daysSupplyNumber === 4 && medType === FertilityCycleMedicationTypeMap.CETROTIDE && firstFill) ||
            daysSupplyNumber === 7
          ) {
            return MEDICATION_TYPES_DISPLAY[medType as FertilityCycleMedicationType];
          }

          return null;
        })
        .filter(Boolean);

      if (excessSupplyMeds.length > 0 && firstFill) {
        showExcessSupplyActionSheet(excessSupplyMeds.length === Object.values(values).length ? [] : excessSupplyMeds);
      } else {
        const medicationsSupplyOnHand: MedicationSupplyOnHand = Object.entries(values).map(([medType, daysSupply]) => {
          const daysSupplyNumber = daysSupply || 0;
          const prescriptionID = prescriptionIDsByMedType[medType as FertilityCycleMedicationType];
          return {
            medication_type: medType as FertilityCycleMedicationType,
            supply_on_hand: {
              prescription_id: prescriptionID,
              days_supply: daysSupplyNumber,
            },
          };
        });

        if (medicationsSupplyOnHand.length) {
          dispatch(updateMedicationsSupplyOnHand(medicationsSupplyOnHand));
        }

        if (nextRouteName) {
          navigate(nextRouteName);
        }

        calculateQuantities();
      }
    },
    [
      calculateQuantities,
      dispatch,
      firstFill,
      navigate,
      nextRouteName,
      prescriptionIDsByMedType,
      showExcessSupplyActionSheet,
    ],
  );

  usePageViewedEvent({
    event: EVENTS.MEDICATION_ON_HAND_VIEWED,
    params: {
      is_refill: isRefill,
    },
  });

  const clearError = useCallback(() => {
    dispatch(clearAutobillPrescriptionError());
    dispatch(clearPrescriptionsError());
    dispatch(clearFetchDeliveriesError());
  }, [dispatch]);

  useEffect(() => {
    clearError();
    return () => {
      clearError();
    };
  }, [clearError]);

  useEffect(() => {
    if (error) {
      addToast(<Toast variant="error">{getErrorMessage(error)}</Toast>);
    }
  }, [addToast, error]);

  const { setValue, watch, handleSubmit } = useForm<Values>({
    defaultValues: initialValues,
    mode: 'onChange',
  });

  const watchedValues = watch();
  const everyFieldHasValue = TypescriptUtils.objectKeys(initialValues).every(
    (medType) => Number(watchedValues[medType]) >= 0,
  );

  return (
    <ProgynyPage
      dismissIcon="chevronleft"
      onDismiss={goBack}
      HeaderContent={
        <ProgynyWizardStepsHeaderContent
          headline={
            firstFill
              ? 'Do you have extra medication to use from a previous cycle?'
              : "How much medication do you have left on hand, including today's dose?"
          }
          subheadline={
            firstFill
              ? 'This helps us create the most efficient order and prevents you from getting charged for medication you won’t need.'
              : 'Add the amounts you have left from your last delivery to create the most efficient and cost-effective order.'
          }
          linkCopy="Are my extra medications still safe to use?"
          linkOnPress={handleShowLeftoverMedsActionSheet}
        />
      }
      buttons={[
        <Button
          key="Save and calculate my order"
          label="Save and calculate my order"
          onPress={() => {
            void handleSubmit(onSubmit)();
          }}
          disabled={!everyFieldHasValue}
          testID="e2e-progyny-save-and-calculate-button"
        />,
      ]}
    >
      <Card>
        <XsSpacing />
        {medicationTypes.map((medicationType) => (
          <LeftoverMedInput
            key={medicationType}
            medicationType={medicationType}
            firstFill={firstFill}
            value={String(watchedValues[medicationType] ?? '')}
            onChange={(v) => {
              setValue(medicationType, Number(v));
            }}
          />
        ))}
        <XsSpacing />
      </Card>
    </ProgynyPage>
  );
};
